import { formatTaskDueDate } from '../../lib/taskCadenceDates';
// The queue model behind the Messages console's left rail.
//
// WHY THIS EXISTS
// MessagesPage listed SMS THREADS. A CRM lists CLIENTS, and a thread is one
// attribute of a client rather than the index. This module is the swap: one
// client list, four sorts over it, identical row shape in every view.
//
// The four views are not four screens. They are one list with different
// ordering and a different reason-for-being-here per row:
//
//   needs_action  the default. Ordered by the ranked scorer already used by the
//                 dashboard widget — who costs us most by being ignored.
//   inbox         only clients whose last message is inbound and unanswered.
//                 Empties out. This is the one that should hit zero.
//   pipeline      grouped by stage, in stage order, with counts. The read you
//                 take twice a day, not the one you work.
//   recent        newest activity first, everyone's. Catching up after a day
//                 away.
//
// DESIGN RULE, and it is the whole point: EVERY ROW HAS THE SAME SHAPE —
// name, why it is here, stage, ONE next action, age. Learn it once and every
// view reads the same. (LANE-3 D-S12: "no per-row declared next action —
// generic icon clusters make the locator reconstruct what 'advance' means".)
//
// No I/O here. MessagesPage fetches; this arranges. Every function is pure and
// testable from fixtures, same contract as yourClientsScore.ts.

import {
  DROPPED_STATUSES,
  scoreClient,
  type ClientSignals,
  type RankedClientRow,
} from "../dashboard/yourClientsScore";
import { actionsByEmphasis, pipelineStatuses, statusActions, statusConfig } from "../clients/config";
import type { ClientStatus } from "../clients/types";

export type QueueViewId = "needs_reply" | "follow_up" | "all" | "needs_action" | "inbox" | "pipeline" | "recent";

/**
 * Ch78 · WHO the queue is about. Grant's IA ruling (2026-09-07): Messages is
 * the clean place for REAL clients — "filter out the ones that never texted
 * back"; everyone else lives in the Leads area.
 *
 * PKT-0693 S2 · THE INTENT IS KEPT, THE MECHANISM IS REVERSED, and Grant ruled
 * the reversal. The ruling is honoured by ORDERING instead of by a mode:
 * `isEngaged`'s four clauses are exactly the four things that lift a row out of
 * the follow-up pile, so a client with none of them is structurally incapable of
 * outranking someone who texted in — without anyone being hidden.
 *
 * Why the mode was worse than the ordering, in this codebase's own words:
 * MessagesPage.tsx said "The SCOPE is part of what the number means
 * ('Needs action: 7' among engaged clients)". A count that needs a comment to be
 * read is a broken count. And the mode PERSISTED 180 days
 * (PERSISTENCE_TTLS.preference) with a default of "engaged", so a fresh session
 * silently hid people and Tuesday's list depended on a tap in August.
 */

/**
 * A client is ENGAGED when there is evidence of a relationship, not merely a
 * row: they texted us · they said we may text them · they moved past
 * new_client · they owe money. A converted lead who never replied and never
 * consented is NOT engaged — that person is still a lead.
 */
export function isEngaged(c: QueueClient): boolean {
  if (c.last_inbound_at) return true;
  if (c.consent_state === "granted") return true;
  if (hasOpenInvoice(c)) return true;
  const status = (c.status || "").toLowerCase();
  return status !== "" && status !== "new_client" && !DROPPED_STATUSES.has(status);
}

/**
 * Ch76 · the money fact the wire has carried since client_queue.py shipped it
 * and the UI dropped on the floor. Defined HERE (not in clientQueueService)
 * because the queue model ranks on it; the service re-exports it.
 */
export interface OpenInvoiceSummary {
  count: number;
  total_amount: number;
  /** Oldest due date across the client's open invoices, ISO date. */
  oldest_due_date: string | null;
}

export interface QueueView {
  id: QueueViewId;
  label: string;
  /** Short line under the switcher explaining what this view is for. */
  hint: string;
}

export const QUEUE_VIEWS: QueueView[] = [
  { id: "needs_reply", label: "Needs reply", hint: "Longest-waiting unanswered messages first." },
  { id: "follow_up", label: "Follow up", hint: "The next existing follow-up, ordered by when it is due." },
  { id: "all", label: "All", hint: "Every client, newest activity first." },
];

/** One client, plus the conversation facts the queue needs to sort and label. */
export interface QueueClient extends ClientSignals {
  first_unanswered_at?: string | null;
  next_task?: { id: string; title: string; owner_name: string | null; owner_user_id: string | null; due_at: string | null; due_date?: string | null } | null;
  /** Last inbound SMS, ISO. Null when the client has never texted in. */
  last_inbound_at?: string | null;
  last_inbound_id?: string | null;
  last_inbound_tie_fingerprint?: string | null;
  last_inbound_preview?: string | null;
  move_in_near?: boolean;
  move_in_date?: string | null;
  next_follow_up?: string | null;
  dismissed?: boolean;
  /** Last outbound SMS, ISO. */
  last_outbound_at?: string | null;
  /**
   * kind/status of that last outbound row, when the feed carries them
   * (migration 065: kind message|media_bounce|help_reply · status
   * failed|undelivered|…). GET /api/clients/queue pre-filters to human,
   * deliverable answers and omits these; feeds that pass raw rows (e.g. the
   * sms-conversations list) set them so isAwaitingReply applies the SAME
   * rule as the backend (_is_human_answer) — PR #453 review F3.
   */
  last_outbound_kind?: string | null;
  last_outbound_status?: string | null;
  /** Preview of the most recent message either direction. */
  last_message_preview?: string | null;
  /** Unread inbound count from the conversation list, when known. */
  unread_count?: number | null;
  /** Open (sent/overdue) invoices — served at GET /api/clients/queue. Absent
   * on feeds that don't carry money (e.g. the sms-conversations list). */
  open_invoice?: OpenInvoiceSummary | null;
  /**
   * Ch78 · may a human text this person right now, per the phone-keyed
   * consent ledger. Served at GET /api/clients/queue (one batched read).
   * "unknown" = nobody has asked; it is NEVER "no". Absent on feeds that
   * predate the field — treated as unknown.
   */
  consent_state?: "granted" | "denied" | "unknown" | null;
  /** HOW they consented: website_form · inbound_first_message ·
   * operator_attested · carrier_start · carrier_stop. Null when unknown. */
  consent_source?: string | null;
  /** Internal (set by buildQueue): this row sits in the gone-cold section. */
  __cold?: boolean;
}

/** The rendered texting-consent chip. `tone` picks the colour; `label` is
 * the whole story in ≤3 words. Absent when consent is unknown — an unknown
 * is not a chip, it is a banner on the thread ("Record consent"). */
export interface TextsSignal {
  label: string;
  tone: "ok" | "off";
}

/** A rendered row. Header rows carry no client and exist to group Pipeline. */
export interface QueueRow {
  kind: "row" | "header";
  /** Stable key for React. Client id for rows, `hdr:<status>` for headers. */
  key: string;
  clientId?: string;
  dismissible?: boolean;
  /** Client name, or the stage label on a header. */
  title: string;
  /** WHY this row is here — the sentence under the name. */
  why: string;
  task?: string;
  /** Stage label + colour, absent on headers. */
  stage?: { label: string; color: string };
  /** The declared next action. A VERB, never an icon. */
  action?: string;
  /** Additional live obligations beyond this row's primary action. */
  additionalObligations?: number;
  /** "3d" · "15d" — the triage fact. */
  age?: string;
  /** True when the age is costing money, not merely old. */
  urgent?: boolean;
  /** Header-only: how many rows follow. */
  count?: number;
  /** Ch76 · "$1,250 owed · 12d overdue" — present iff the client has open
   * invoices. The rail renders it as a money chip. */
  invoice?: string;
  /** True when the oldest open invoice is past due. Drives the chip colour
   * and the ranking boost. */
  invoiceOverdue?: boolean;
  /** Ch78 · texting consent, rendered FIRST in the chip row. Grant: "he
   * checked YES to SMS on the lead form — why isn't that the first thing I
   * see?" Present only when the ledger has an answer. */
  texts?: TextsSignal;
  /** Ch78 · which needs_action section this row (or header) belongs to. */
  group?: QueueGroup;
  /** False for rows the badge must NOT count (gone cold). Default true. */
  counted?: boolean;
  /** F24 · an operable TEST client: listed (with a TEST tag), never counted. */
  test?: boolean;
  /** Header-only: the rail can fold this section. */
  collapsible?: boolean;
}

/**
 * Ch78 · the needs_action SECTIONS. Grant, reading "Needs action 59": the
 * badge counted every engaged client, because the scorer's stage weight makes
 * every score > 0 and the view was a RANKING, not a filter. Now:
 *   reply     they texted and we owe an answer
 *   money     an open invoice
 *   followup  something live — a pending step, never contacted, move-in
 *             soon, or touched inside COLD_DAYS
 *   cold      nothing has moved in COLD_DAYS — one check-in text each, folded
 *             by default and NEVER in the badge
 */
export type QueueGroup = "reply" | "money" | "followup" | "cold";

/** Days without contact in either direction before a client is "gone cold". */
export const COLD_DAYS = 30;

/** The cold pile's ONE action, and the draft it opens the composer with.
 * A question, not a pitch: it either revives them or tells you to move them
 * to out-of-contact. The human edits and taps Send. */
export const CHECK_IN_ACTION = "Check in";
export function checkInDraft(firstName: string | null | undefined): string {
  const name = (firstName || "").trim();
  return `Hi${name ? ` ${name}` : ""} — it's LocateNow. Still looking for a place? If so I'll send you fresh options; if not, no worries at all.`;
}

export function isGoneCold(c: QueueClient, nowIso: string): boolean {
  if (isAwaitingReply(c) || hasOpenInvoice(c)) return false;
  if ((c.pending_task_count ?? 0) > 0) return false;
  const inbound = daysSince(c.last_inbound_at, nowIso);
  if (inbound !== null && inbound < COLD_DAYS) return false;
  // Never contacted is fresh WORK, not cold — the queue has no created_at to
  // say otherwise, and guessing "cold" would hide a lead who said yes.
  const touch = lastTouch(c);
  if (!touch) return false;
  const d = daysSince(touch, nowIso);
  return d !== null && d >= COLD_DAYS;
}

const DAY = 86_400_000;

export function ageLabel(iso: string | null | undefined, nowIso: string): string {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const now = new Date(nowIso).getTime();
  if (isNaN(then) || isNaN(now)) return "—";
  const mins = Math.max(0, Math.round((now - then) / 60_000));
  if (mins < 60) return `${mins}m`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.round(hrs / 24)}d`;
}

function deadlineAge(iso: string | null | undefined, nowIso: string): string {
  if (!iso) return "—";
  const deadline = Date.parse(iso);
  const now = Date.parse(nowIso);
  if (!Number.isFinite(deadline) || !Number.isFinite(now)) return "—";
  if (deadline <= now) return ageLabel(iso, nowIso);
  const mins = Math.ceil((deadline - now) / 60_000);
  if (mins < 60) return `in ${mins}m`;
  const hours = Math.ceil(mins / 60);
  if (hours < 24) return `in ${hours}h`;
  return `in ${Math.ceil(hours / 24)}d`;
}

/** Days since `iso`, or null when absent/unparseable. */
export function daysSince(iso: string | null | undefined, nowIso: string): number | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  const now = new Date(nowIso).getTime();
  if (isNaN(then) || isNaN(now)) return null;
  return Math.max(0, Math.floor((now - then) / DAY));
}

/**
 * F3 (PR #453 review): what counts as a HUMAN answer. Mirrors the backend's
 * _is_human_answer in backend/api/routes/client_queue.py — change both
 * together. Automated copy (media_bounce/help_reply) and carrier-refused
 * sends (failed/undelivered) never clear the awaiting-reply flag.
 */
export function isEffectiveAnswer(
  kind?: string | null,
  status?: string | null,
): boolean {
  if (kind === "media_bounce" || kind === "help_reply") return false;
  if (status === "failed" || status === "undelivered") return false;
  return true;
}

/** A client is waiting on us when their last inbound is newer than our last
 * HUMAN, deliverable outbound (see isEffectiveAnswer). */
export function isAwaitingReply(c: QueueClient): boolean {
  if (c.first_unanswered_at !== undefined) return Boolean(c.first_unanswered_at);
  if (/^(STOP|STOPALL|UNSUBSCRIBE|CANCEL|END|QUIT|START|UNSTOP|YES|HELP|INFO)[.! ]*$/i.test((c.last_message_preview || '').trim())) return false;
  if (!c.last_inbound_at) return false;
  const effectiveOut =
    c.last_outbound_at &&
    isEffectiveAnswer(c.last_outbound_kind, c.last_outbound_status)
      ? c.last_outbound_at
      : null;
  if (!effectiveOut) return true;
  return new Date(c.last_inbound_at).getTime() > new Date(effectiveOut).getTime();
}

/**
 * The declared next action for a client.
 *
 * `statusActions` already maps status -> the actions that make sense at that
 * stage; it has been feeding the rail since the two-way SMS lane shipped. What
 * it does NOT do is pick ONE, and a list of four equal buttons is the thing
 * this queue exists to stop. Awaiting-reply always wins — a client who texted
 * and got nothing back outranks any stage heuristic.
 */
export function nextActionFor(c: QueueClient): string {
  if (isAwaitingReply(c)) return c.consent_state === "denied" ? "Review" : "Reply";
  if ((c.pending_task_count ?? 0) > 0) return "Review task";
  // Emphasis order, not display order: New Client and Touring share one list
  // (PKT-0841) but still recommend what they recommended before.
  const acts = actionsByEmphasis((c.status || "") as ClientStatus);
  const first = acts.length > 0 ? acts[0].label : "Review";
  // Ch78 · never advertise a text to someone with a STOP on record. The send
  // path would refuse it anyway; the rail should not point at a dead door.
  // The next declared action that is not a text wins; else "Call".
  if (c.__cold) return CHECK_IN_ACTION;
  if (first === "Send Text" && c.consent_state === "denied") {
    const alt = acts.find((a) => a.label !== "Send Text");
    return alt ? alt.label : "Call";
  }
  return first;
}

/** Ch78 · the texting-consent chip for a row, or undefined when unknown. */
export function textsFor(c: QueueClient): TextsSignal | undefined {
  if (c.consent_state === "granted") {
    return { label: c.consent_source === "website_form" ? "Texts OK · form" : "Texts OK", tone: "ok" };
  }
  if (c.consent_state === "denied") return { label: "No texts (STOP)", tone: "off" };
  return undefined;
}

export function taskLabel(c: QueueClient, nowIso: string): string | undefined {
  if (!(c.pending_task_count ?? 0)) return undefined;
  const due = c.next_task?.due_at || c.next_task?.due_date || c.next_task_due_at;
  const parsed = due ? Date.parse(due) : NaN;
  const date = Number.isFinite(parsed) ? formatTaskDueDate(due!) : null;
  const when = date ? `${parsed <= Date.parse(nowIso) ? 'Overdue' : 'Due'} ${date}` : 'No due date';
  const owner = c.next_task?.owner_name || (c.next_task?.owner_user_id ? 'Assigned teammate' : 'Unassigned');
  return `${c.next_task?.title || 'Follow-up task'} · ${owner} · ${when}`;
}

/** Why this row is in front of you, in one line. Never generic. */
export function whyFor(c: QueueClient, nowIso: string): string {
  if (isAwaitingReply(c)) {
    const preview = (c.last_inbound_preview || c.last_message_preview || "").trim();
    return preview ? `“${preview}”` : "Waiting on your reply";
  }
  const task = taskLabel(c, nowIso);
  if (task) return task;
  const sinceContact = daysSince(c.last_contacted_at, nowIso);
  if (sinceContact === null) {
    // Ch78 · a never-contacted person who asked to be texted is the cheapest
    // next action on the board. Say so in the one sentence they get.
    return c.consent_state === "granted" ? "Never contacted · asked to be texted" : "Never contacted";
  }
  if (c.__cold) {
    const d = daysSince(lastTouch(c), nowIso);
    return `No contact in ${d} days · gone cold`;
  }
  if (sinceContact >= 30) return `No contact in ${sinceContact} days`;
  if ((c.pending_task_count ?? 0) > 0) {
    const n = c.pending_task_count as number;
    return n === 1 ? "1 follow-up due" : `${n} follow-ups due`;
  }
  const budget = c.max_budget ? `$${Number(c.max_budget).toLocaleString()}` : null;
  return budget ? `${budget} · ${statusLabel(c.status)}` : statusLabel(c.status);
}

function statusLabel(status: string | null | undefined): string {
  const cfg = statusConfig[(status || "") as ClientStatus];
  return cfg?.label || status || "—";
}

function stageOf(c: QueueClient): { label: string; color: string } | undefined {
  const cfg = statusConfig[(c.status || "") as ClientStatus];
  if (!cfg) return undefined;
  return { label: cfg.label, color: cfg.color };
}

/**
 * Age is red when it is COSTING something, not merely when it is large.
 * An application sitting 15 days is money at risk. A cold lead at 27 days is
 * not, and colouring both red is how a red badge stops meaning anything.
 */
function isUrgent(c: QueueClient, nowIso: string): boolean {
  if ((c.overdue_task_count ?? 0) > 0) return true;
  if (c.__cold) return false; // old is not the same as costing money
  if (isAwaitingReply(c)) {
    const d = daysSince(c.first_unanswered_at || c.last_inbound_at, nowIso);
    return d !== null && d >= 1;
  }
  const late = daysSince(c.last_contacted_at, nowIso);
  const moneyStage = c.status === "applied" || c.status === "touring";
  if (moneyStage && late !== null && late >= 7) return true;
  return c.last_contacted_at == null && (c.max_budget ?? 0) > 0;
}

/** Ch76 · does this client owe us money right now? */
export function hasOpenInvoice(c: QueueClient): boolean {
  return (c.open_invoice?.count ?? 0) > 0 && (c.open_invoice?.total_amount ?? 0) > 0;
}

interface FollowUpTrigger {
  at: string;
  why: string;
  action: string;
  rank: number;
}

/** Existing dated follow-up obligations, in the same priority order used by Follow up. */
function followUpTriggers(c: QueueClient, nowIso: string): FollowUpTrigger[] {
  const taskDue = c.next_task_due_at || c.next_task?.due_at || c.next_task?.due_date || null;
  const candidates: Array<Omit<FollowUpTrigger, "at"> & { at: string | null | undefined }> = [
    { at: taskDue, why: c.next_task?.title || `${c.pending_task_count || 1} follow-up${c.pending_task_count === 1 ? "" : "s"} due`, action: "Review task", rank: 0 },
    { at: c.next_follow_up, why: "Follow-up due", action: "Follow up", rank: 1 },
    { at: hasOpenInvoice(c) ? c.open_invoice?.oldest_due_date : null, why: "Payment follow-up", action: "Review payment", rank: 2 },
    ...(c.move_in_near ? [{ at: c.move_in_date || nowIso, why: "Move-in approaching", action: "Review move-in", rank: 3 }] : []),
  ];
  return candidates
    .filter((trigger): trigger is FollowUpTrigger => Boolean(trigger.at && Number.isFinite(Date.parse(trigger.at))))
    .sort((a, b) => Date.parse(a.at) - Date.parse(b.at) || a.rank - b.rank);
}

/** Count task obligations even when their next task has no due timestamp. */
function followUpObligationCount(c: QueueClient, nowIso: string): number {
  const triggers = followUpTriggers(c, nowIso);
  const hasDatedTask = triggers.some((trigger) => trigger.rank === 0);
  const taskCount = Math.max(c.pending_task_count ?? 0, hasDatedTask ? 1 : 0);
  return taskCount + triggers.filter((trigger) => trigger.rank !== 0).length;
}

/** Ch76 · true when the oldest open invoice's due date is behind `nowIso`. */
export function isInvoiceOverdue(c: QueueClient, nowIso: string): boolean {
  if (!hasOpenInvoice(c)) return false;
  const days = daysSince(c.open_invoice?.oldest_due_date, nowIso);
  return days !== null && days > 0;
}

/**
 * Ch76 · the money line for a row: "$1,250 owed" or "$1,250 owed · 12d
 * overdue". Never invents precision — an invoice set with no parseable due
 * date still says what is owed.
 */
export function invoiceLabel(c: QueueClient, nowIso: string): string | undefined {
  if (!hasOpenInvoice(c)) return undefined;
  const inv = c.open_invoice as OpenInvoiceSummary;
  const amount = `$${Number(inv.total_amount).toLocaleString()}`;
  const overdueDays = isInvoiceOverdue(c, nowIso)
    ? daysSince(inv.oldest_due_date, nowIso)
    : null;
  return overdueDays !== null && overdueDays > 0
    ? `${amount} owed · ${overdueDays}d overdue`
    : `${amount} owed`;
}

function toRow(c: QueueClient, nowIso: string, ageFrom?: string | null): QueueRow {
  const additionalObligations = isAwaitingReply(c) ? followUpObligationCount(c, nowIso) : 0;
  return {
    kind: "row",
    key: c.id,
    clientId: c.id,
    title: [c.first_name, c.last_name].filter(Boolean).join(" ").trim() || "Unnamed client",
    why: whyFor(c, nowIso),
    task: isAwaitingReply(c) ? taskLabel(c, nowIso) : undefined,
    stage: stageOf(c),
    action: nextActionFor(c),
    additionalObligations: additionalObligations || undefined,
    age: ageLabel(ageFrom ?? c.last_contacted_at, nowIso),
    urgent: isUrgent(c, nowIso) || isInvoiceOverdue(c, nowIso),
    invoice: invoiceLabel(c, nowIso),
    invoiceOverdue: isInvoiceOverdue(c, nowIso) || undefined,
    texts: textsFor(c),
  };
}

function lastTouch(c: QueueClient): string | null {
  const times = [c.last_inbound_at, c.last_outbound_at, c.last_contacted_at]
    .filter((v): v is string => typeof v === "string" && !!v)
    .map((v) => new Date(v).getTime())
    .filter((n) => !isNaN(n));
  if (times.length === 0) return null;
  return new Date(Math.max(...times)).toISOString();
}

/**
 * Build the rows for one view.
 *
 * `needs_action` delegates ordering to the same scorer the dashboard widget
 * uses, so the two surfaces cannot disagree about who is urgent — one ranking,
 * two renderings. Clients the scorer drops (closed, out_of_contact) stay
 * dropped here.
 */
/**
 * F24 (migration 122) · a test client stays usable — its row is listed so
 * texting and actions can be exercised — but it is excluded from every count:
 * the view badges (queueCount), the stage header counts, and DaySummary.
 * Applied once here so no individual view can forget it.
 */
export function buildQueue(
  clients: QueueClient[],
  view: QueueViewId,
  nowIso: string,
): QueueRow[] {
  const testIds = new Set(clients.filter((c) => c.is_test_client === true).map((c) => c.id));
  const rows = buildQueueRows(clients, view, nowIso);
  if (!testIds.size) return rows;
  let header: QueueRow | null = null;
  return rows.map((row) => {
    if (row.kind === "header") {
      header = { ...row };
      return header;
    }
    if (!row.clientId || !testIds.has(row.clientId)) return row;
    if (header && typeof header.count === "number") header.count = Math.max(0, header.count - 1);
    return { ...row, test: true, counted: false };
  });
}

function buildQueueRows(
  clients: QueueClient[],
  view: QueueViewId,
  nowIso: string,
): QueueRow[] {
  if (view === "follow_up") {
    const dueAt = (c: QueueClient): number => {
      const taskDeadline = c.next_task_due_at || c.next_task?.due_at || c.next_task?.due_date;
      const moveInDeadline = c.move_in_near ? c.move_in_date || nowIso : null;
      const values = [taskDeadline, c.next_follow_up, hasOpenInvoice(c) ? c.open_invoice?.oldest_due_date : null, moveInDeadline]
        .filter((v): v is string => Boolean(v));
      const parsed = values.map((v) => new Date(v).getTime()).filter(Number.isFinite);
      return parsed.length ? Math.min(...parsed) : Number.POSITIVE_INFINITY;
    };
    return clients
      .filter((c) => !isAwaitingReply(c) && !c.dismissed && (!DROPPED_STATUSES.has((c.status || "").toLowerCase()) || (c.pending_task_count ?? 0) > 0))
      .filter((c) => dueAt(c) !== Number.POSITIVE_INFINITY || (c.pending_task_count ?? 0) > 0 || isGoneCold(c, nowIso) || c.move_in_near || (!c.last_contacted_at && c.consent_state === "granted"))
      .sort((a, b) => dueAt(a) - dueAt(b) || a.id.localeCompare(b.id))
      .map((c) => {
        const rowClient = isGoneCold(c, nowIso) ? { ...c, __cold: true } : c;
        const triggers = followUpTriggers(c, nowIso);
        const winner = triggers[0];
        const fallbackWhy = isGoneCold(c, nowIso) ? toRow(rowClient, nowIso).why : (c.pending_task_count ?? 0) > 0 ? `${c.pending_task_count} follow-up${c.pending_task_count === 1 ? "" : "s"} due` : !c.last_contacted_at && c.consent_state === "granted" ? "Never contacted · asked to be texted" : "Follow up";
        const ageFrom = winner?.at || c.last_contacted_at;
        const base = toRow(rowClient, nowIso, ageFrom);
        const fallbackAction = rowClient.__cold ? CHECK_IN_ACTION : nextActionFor(c);
        const futureSchedule = winner?.rank === 1 && Date.parse(winner.at) > Date.parse(nowIso)
          ? new Date(winner.at)
          : null;
        const winnerWhy = futureSchedule
          ? `Follow-up scheduled ${futureSchedule.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" })} at ${futureSchedule.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZoneName: "short", timeZone: "UTC" })}`
          : winner?.why;
        const hasPrimaryObligation = Boolean(winner) || (c.pending_task_count ?? 0) > 0;
        const additionalObligations = Math.max(
          0,
          followUpObligationCount(c, nowIso) - (hasPrimaryObligation ? 1 : 0),
        );
        return { ...base, why: winnerWhy || fallbackWhy, action: winner?.action || fallbackAction, additionalObligations: additionalObligations || undefined, age: deadlineAge(ageFrom, nowIso), task: undefined, stage: undefined, invoice: undefined, texts: undefined, dismissible: true, counted: true, group: rowClient.__cold ? "cold" : hasOpenInvoice(c) ? "money" : "followup" };
      });
  }
  if (view === "all") {
    return [...clients].sort((a, b) =>
      new Date(lastTouch(b) || b.created_at || 0).getTime() - new Date(lastTouch(a) || a.created_at || 0).getTime()
    ).map(c => toRow(c, nowIso, lastTouch(c) || c.created_at));
  }
  if (view === "inbox" || view === "needs_reply") {
    return clients
      .filter(isAwaitingReply)
      .sort(
        (a, b) =>
          new Date((a.first_unanswered_at || a.last_inbound_at) as string).getTime() -
          new Date((b.first_unanswered_at || b.last_inbound_at) as string).getTime(),
      )
      .map((c) => toRow(c, nowIso, c.first_unanswered_at || c.last_inbound_at));
  }

  if (view === "recent") {
    return clients
      .map((c) => ({ c, t: lastTouch(c) }))
      .filter((x) => x.t !== null)
      .sort((a, b) => new Date(b.t as string).getTime() - new Date(a.t as string).getTime())
      .map((x) => toRow(x.c, nowIso, x.t));
  }

  if (view === "pipeline") {
    const out: QueueRow[] = [];
    for (const stage of pipelineStatuses) {
      const inStage = clients.filter((c) => c.status === stage);
      if (inStage.length === 0) continue;
      const cfg = statusConfig[stage];
      out.push({
        kind: "header",
        key: `hdr:${stage}`,
        title: cfg?.label || stage,
        why: "",
        count: inStage.length,
        stage: cfg ? { label: cfg.label, color: cfg.color } : undefined,
      });
      inStage
        .sort((a, b) => scoreClient(b, nowIso).score - scoreClient(a, nowIso).score)
        .forEach((c) => out.push(toRow(c, nowIso)));
    }
    return out;
  }

  // needs_action — the default. Sectioned (Ch78): reply · money · follow up ·
  // gone cold. The DROPPED set still applies (closed / out_of_contact never
  // appear). Gone-cold rows render folded and are NOT counted.
  const live = clients.filter((c) => (!DROPPED_STATUSES.has((c.status || "").toLowerCase()) || isAwaitingReply(c) || (c.pending_task_count ?? 0) > 0));
  const score = (c: QueueClient) => scoreClient(c, nowIso).score;
  const t = (iso: string | null | undefined) => (iso ? new Date(iso).getTime() : 0);

  const reply = live.filter(isAwaitingReply).sort((a, b) => t(a.first_unanswered_at || a.last_inbound_at) - t(b.first_unanswered_at || b.last_inbound_at));
  const money = live
    .filter((c) => !isAwaitingReply(c) && hasOpenInvoice(c))
    .sort((a, b) => {
      const ov = Number(isInvoiceOverdue(b, nowIso)) - Number(isInvoiceOverdue(a, nowIso));
      return ov !== 0 ? ov : score(b) - score(a);
    });
  const rest = live.filter((c) => !isAwaitingReply(c) && !hasOpenInvoice(c));
  const cold = rest
    .filter((c) => isGoneCold(c, nowIso))
    .map((c) => ({ ...c, __cold: true }))
    .sort((a, b) => t(lastTouch(b)) - t(lastTouch(a)));
  const followup = rest
    .filter((c) => !isGoneCold(c, nowIso) && score(c) > 0)
    .sort((a, b) => {
      const due = Number((b.overdue_task_count ?? 0) > 0) - Number((a.overdue_task_count ?? 0) > 0);
      return due || score(b) - score(a);
    });

  const out: QueueRow[] = [];
  const section = (
    group: QueueGroup,
    title: string,
    members: QueueClient[],
    opts: { counted?: boolean; collapsible?: boolean; ageFrom?: (c: QueueClient) => string | null | undefined } = {},
  ) => {
    if (members.length === 0) return;
    out.push({
      kind: "header",
      key: `hdr:${group}`,
      title,
      why: "",
      count: members.length,
      group,
      collapsible: opts.collapsible,
      counted: opts.counted,
    });
    for (const c of members) {
      out.push({ ...toRow(c, nowIso, opts.ageFrom?.(c)), group, counted: opts.counted });
    }
  };
  section("reply", "Reply", reply, { ageFrom: (c) => c.first_unanswered_at || c.last_inbound_at });
  section("money", "Money", money);
  section("followup", "Follow up", followup);
  section("cold", "Gone cold", cold, { counted: false, collapsible: true });
  return out;
}

/** Count for a view's badge. Headers never count; neither do folded
 * gone-cold rows (`counted === false`). */
export function queueCount(rows: QueueRow[]): number {
  return rows.filter((r) => r.kind === "row" && r.counted !== false).length;
}
